import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchUserDashboard } from '../../../../api/userDashboardApi';
import './TrainingDashboard.css';

const TrainingDashboard = () => {
  const [trainingData, setTrainingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    english: true,
    hindi: true
  });

  // Separate training videos by language
  const getLanguageSpecificTraining = (language) => {
    return trainingData.filter(training =>
      training.tvc.some(video => video.language === language)
    ).map(training => ({
      ...training,
      // Only include videos of specified language
      tvc: training.tvc.filter(video => video.language === language)
    }));
  };

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const fetchDashboard = async () => {
      try {
        const dashboard = await fetchUserDashboard(userId);
        setTrainingData(dashboard?.training || []);
      } catch (error) {
        // console.error('Error fetching dashboard data:', error);
        setTrainingData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const areAllTrainingsPOSP = () => {
    return trainingData.every(training => training.examTypeId === 1);
  };

  const areAllTrainingsNISM = () => {
    return trainingData.every(training => training.examTypeId !== 1);
  };

  // title based on training type
  const getSectionTitle = (language) => {
    if (!areAllTrainingsPOSP() && !areAllTrainingsNISM()) {
      return null; 
    }
    return areAllTrainingsPOSP()
      ? `POSP Training Video/PDF (${language})`
      : `NISM 5A Pre-recorded Video (${language})`;
  };

  return (
    <div className="training-exam-container">
      <h2 className='heading-for-training-page-user' style={{ textAlign: 'start' }}>🎗️ Training</h2>
      {loading ? (
        <div className="spinner-container">
          <div className="spinner"></div>
        </div>
      ) : trainingData.length === 0 ? (
        <div className="spinner-container">
          <div className="no-data-user-training-dashboard">Relax! There Are No Training for You!</div>
        </div>
      ) : (
        <>
          {!areAllTrainingsPOSP() && !areAllTrainingsNISM() ? (
            // Show all training cards directly when mixed content
            <div className="training-container">
              {getLanguageSpecificTraining('ENGLISH').map((training) => (
                <div className="insurance-card" key={training.trainingId}>
                  <div className="card-header">
                    <h2>{training.trainingName}</h2>
                    <div className="category">
                      <Link to={`/user/training/${training.trainingId}`}>
                        <button className="go-to-training-btn">View Training</button>
                      </Link>
                    </div>
                  </div>
                  <div className="policy-details">
                    <div className="detail-row">
                      <span className="label">Description:</span>
                      <span className="value">{training.trainingDescription}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Duration (mins):</span>
                      <span className="value">{training.trainingDuration}</span>
                    </div>
                    <div className={`status ${training.trainingstatus === 'not started' ? 'inactive' : 'active'}`}>
                      {training.trainingstatus}
                    </div>
                  </div>
                </div>
              ))}
              {getLanguageSpecificTraining('HINDI').map((training) => (
                <div className="insurance-card" key={training.trainingId}>
                  <div className="card-header">
                    <h2>{training.trainingName}</h2>
                    <div className="category">
                      <Link to={`/user/training/${training.trainingId}`}>
                        <button className="go-to-training-btn">Watch Training</button>
                      </Link>
                    </div>
                  </div>
                  <div className="policy-details">
                    <div className="detail-row">
                      <span className="label">Description:</span>
                      <span className="value">{training.trainingDescription}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Duration (mins):</span>
                      <span className="value">{training.trainingDuration}</span>
                    </div>
                    <div className={`status ${training.trainingstatus === 'not started' ? 'inactive' : 'active'}`}>
                      {training.trainingstatus}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Show expandable sections for single type content
            <>
              {/* English Training Section */}
              <div className="training-section">
                <div
                  className="training-section-header"
                  onClick={() => toggleSection('english')}
                >
                  {getSectionTitle('English') && (
                    <h3 className='training-section-language-h3'>
                      {getSectionTitle('English')}
                      <span className="accordion-icon">
                        {expandedSections.english ? '▼' : '▶'}
                      </span>
                    </h3>
                  )}
                </div>
                {expandedSections.english && (
                  <div className="training-container">
                    {getLanguageSpecificTraining('ENGLISH').map((training) => (
                      <div className="insurance-card" key={training.trainingId}>
                        <div className="card-header">
                          <h2>{training.trainingName}</h2>
                          <div className="category">
                            <Link to={`/user/training/${training.trainingId}`}>
                              <button className="go-to-training-btn">View Training</button>
                            </Link>
                          </div>
                        </div>
                        <div className="policy-details">
                          <div className="detail-row">
                            <span className="label">Description:</span>
                            <span className="value">{training.trainingDescription}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Duration (mins):</span>
                            <span className="value">{training.trainingDuration}</span>
                          </div>
                          <div className={`status ${training.trainingstatus === 'not started' ? 'inactive' : 'active'}`}>
                            {training.trainingstatus}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Hindi Training Section */}
              {getLanguageSpecificTraining('HINDI').length > 0 && (
                <div className="training-section">
                  <div
                    className="training-section-header"
                    onClick={() => toggleSection('hindi')}
                  >
                    {getSectionTitle('Hindi') && (
                      <h3 className='training-section-language-h3'>
                        {getSectionTitle('Hindi')}
                        <span className="accordion-icon">
                          {expandedSections.hindi ? '▼' : '▶'}
                        </span>
                      </h3>
                    )}
                  </div>
                  {expandedSections.hindi && (
                    <div className="training-container">
                      {getLanguageSpecificTraining('HINDI').map((training) => (
                        <div className="insurance-card" key={training.trainingId}>
                          <div className="card-header">
                            <h2>{training.trainingName}</h2>
                            <div className="category">
                              <Link to={`/user/training/${training.trainingId}`}>
                                <button className="go-to-training-btn">Watch Training</button>
                              </Link>
                            </div>
                          </div>
                          <div className="policy-details">
                            <div className="detail-row">
                              <span className="label">Description:</span>
                              <span className="value">{training.trainingDescription}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Duration (mins):</span>
                              <span className="value">{training.trainingDuration}</span>
                            </div>
                            <div className={`status ${training.trainingstatus === 'not started' ? 'inactive' : 'active'}`}>
                              {training.trainingstatus}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TrainingDashboard;
